import { useEffect } from 'react'

import {
  useTrackProductGroupImpression,
  useTrackProductImpression,
} from '../../../../app/_providers/TrackingProvider.client'
import { useAccentColorSetter } from '../../../../containers/AccentColorContainer'
import { useGoogleTagManager } from '../../../../lib/hooks/useGoogleTagManager'

import { usePdpServerSideProps } from './PdpServerSidePropsProvider'

export const ProductPageTrackingEffects = () => {
  const { data } = usePdpServerSideProps()
  const { setAccentColor } = useAccentColorSetter()
  const { pushProductClick, pushProductDetailsImpression } = useGoogleTagManager()
  const { trackProductImpression } = useTrackProductImpression()
  const { trackProductGroupImpression } = useTrackProductGroupImpression()

  useEffect(() => {
    setAccentColor(data?.product.variantGroup.color?.hexCodes)
  }, [data?.product.variantGroup.color?.hexCodes, setAccentColor])

  const variant = data?.product.variantGroup.variant
  const productHandle = data?.product.handle
  const productCategory = data?.product.variantGroup.primaryCategory.id
  const variantGroup = data?.product.variantGroup

  // This is technically not an accurate piece of code. A use effect based on `selectedVariant`
  // can technically fire without someone clicking something, if the shared URL includes
  // the size query param or if the product only has one size... so I think this should technically
  // be refactored and triggered on click of something maybe rather than via a useEffect :shrug:
  useEffect(() => {
    if (!variant || !productHandle || !productCategory) return
    pushProductClick({
      variant: {
        ...variant,
        product: {
          handle: productHandle,
        },
        variantGroup: {
          primaryCategory: {
            id: productCategory,
          },
        },
      },
    })
  }, [productCategory, productHandle, pushProductClick, variant])

  // As of now, this uses `variant` instead of `product`. But it fires only on product change
  // and it only uses the first variant of that product. So it basically makes no sense and
  // likely should be altered to make the impression based on the `featuredProduct` without
  // looking at the first variant at all.
  useEffect(() => {
    if (!variant || !productHandle || !productCategory || !variantGroup) return
    pushProductDetailsImpression({
      variant: {
        ...variant,
        product: {
          handle: productHandle,
        },
        variantGroup: {
          primaryCategory: {
            id: productCategory,
          },
        },
      },
    })

    trackProductImpression({
      handle: productHandle,
      id: variantGroup.id,
      shopifyId: variant.shopifyProductId,
      featuredProduct: true,
      soldOut: variantGroup.stockState === 'OUT_OF_STOCK',
      color: variantGroup.color?.id ?? '',
      productGroupHandle: productHandle,
      genderCategory: variantGroup.gender,
      variantInformation: variantGroup.variants.map(variant => ({
        size: variant.size?.id ?? '',
        soldOut: variant.stockState === 'OUT_OF_STOCK',
        discounted: !!variant.compareAtPrice,
      })),
    })
  }, [
    variant,
    productCategory,
    productHandle,
    pushProductDetailsImpression,
    trackProductImpression,
    variantGroup,
  ])

  useEffect(() => {
    if (!productHandle) return
    trackProductGroupImpression({
      handle: productHandle,
    })
  }, [trackProductGroupImpression, productHandle])

  return <></>
}
